// Search box for list toolbars (Stores, Team, Territories): magnifier, clear button, Escape clears, and an optional
// "/" shortcut that focuses it from anywhere on the page (the console's equivalent of pulling down to search).

import { Search, X } from 'lucide-react';
import { useEffect, useRef, type InputHTMLAttributes, type Ref } from 'react';
import { cn } from './cn';
import { Input, type ControlSize } from './Field';
import { isModalOpen } from './focus';

export interface SearchFieldProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange' | 'type' | 'size'> {
  value: string;
  onValueChange: (value: string) => void;
  /** Accessible name; also the placeholder unless one is given. */
  label?: string;
  size?: ControlSize;
  /** Focus this field when "/" is pressed outside any text control. */
  slashShortcut?: boolean;
  ref?: Ref<HTMLInputElement>;
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

export function SearchField({
  value,
  onValueChange,
  label = 'Search',
  placeholder,
  size = 'md',
  slashShortcut = false,
  className,
  onKeyDown,
  ref,
  ...rest
}: SearchFieldProps) {
  const inner = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!slashShortcut) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      // Not while a modal dialog is open: the field behind it is inert.
      if (isModalOpen()) return;
      event.preventDefault();
      inner.current?.focus();
      inner.current?.select();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [slashShortcut]);

  const setRef = (node: HTMLInputElement | null) => {
    inner.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) (ref as { current: HTMLInputElement | null }).current = node;
  };

  return (
    <Input
      ref={setRef}
      type="search"
      role="searchbox"
      size={size}
      value={value}
      aria-label={label}
      placeholder={placeholder ?? label}
      autoComplete="off"
      spellCheck={false}
      enterKeyHint="search"
      onChange={(e) => onValueChange(e.target.value)}
      onKeyDown={(e) => {
        onKeyDown?.(e);
        if (e.defaultPrevented) return;
        if (e.key === 'Escape' && value !== '') {
          // Clear first; a second Escape can then close whatever contains the field.
          e.preventDefault();
          e.stopPropagation();
          onValueChange('');
        }
      }}
      leadingIcon={<Search />}
      trailing={
        value !== '' ? (
          <button
            type="button"
            aria-label="Clear search"
            title="Clear"
            onClick={() => {
              onValueChange('');
              inner.current?.focus();
            }}
            className="flex size-7 items-center justify-center rounded-full text-fg-subtle hover:bg-surface-3 hover:text-fg"
          >
            <X aria-hidden className="size-3.5" />
          </button>
        ) : slashShortcut ? (
          <kbd
            aria-hidden
            className="mr-1.5 hidden rounded-md border border-line-strong px-1.5 font-sans text-[11px] text-fg-subtle sm:inline"
          >
            /
          </kbd>
        ) : undefined
      }
      {...rest}
      className={cn('w-full', className)}
    />
  );
}
